/*!
// <copyright file="PowerApps.ts" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import * as $ from "jquery";
import { appState } from "../AppState";
import { app } from "../DynamicsApp";
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { appletInteraction } from "../AppletInteraction";
import { messaging, util, $format } from "@ax/office-shared-lib";
import { powerAppsResources } from "@ax/office-cdn-app-resources";
import { getEnvironmentFromUrl, Environment } from "./OriginRegEx";
import { TelemetryConnectionStrings } from "../Telemetry/TelemetryConstants";

/** Encapsulates PowerApps specific logic. */
export namespace powerApps {

    /** The versions of PowerApps supported. */
    export enum PowerAppsVersion {

        /** Represents legacy PowerApps. */
        CDS1 = <any>"CDS1",

        /** Represents PowerApps against XRM. */
        CDS2 = <any>"CDS2"
    }

    /**
     * Executed once the initial add-in is initialized, continuing the connection flow appropriately based on configured settings.
     * @param originalPostInitialization Function that performs non-PowerApps post-initialization
     */
    export function initialized(originalPostInitialization: () => void): void {
        originalPostInitialization();

        // Set source for Copilot for finance logo image element
        app._copilotForFinanceBannerLogo.attr("src", "../../Images/CopilotForFinanceLogo_32.png");
    }

    /**
     * Messages resource requests to handle converting dot notation label IDs and failing all configuration resource requests.
     * @param resourceID The ID of the resource to which the request is being sent.
     * @param message The message being sent.
     */
    export function massageResourceRequests(resourceID: string, message: messaging.BaseRequest): void {

        if (resourceID === appletInteraction.WellKnownResources.Data.ID && message.Type === messaging.MessageTypes.getLabelsRequest) {
            const labelsRequest = message as messaging.Requests.GetLabelsRequest;
            const labelMessageMapping = new util.Map();
            const correctID = (value) => { return value.replace(/\./g, ":"); };
            labelsRequest.LabelIDs = labelsRequest.LabelIDs.map((value, index, array) => {
                let replacedValue = value;
                if (messaging.LabelIdValidationRegex.test(value)) {
                    replacedValue = correctID(value);
                } else if (messaging.FormattedLabelExpression.test(value)) {
                    try {
                        const formattedLabel = JSON.parse(value);
                        formattedLabel.FormatLabelId = appletInteraction.LabelIdValidationRegex.test(formattedLabel.FormatLabelId) ? correctID(formattedLabel.FormatLabelId) : formattedLabel.FormatLabelId;
                        formattedLabel.ParameterLabelIds = (formattedLabel.ParameterLabelIds as string[]).map((parameterValue, parameterIndex, parameterArray) => {
                            return appletInteraction.LabelIdValidationRegex.test(parameterValue) ? correctID(parameterValue) : parameterValue;
                        });
                        replacedValue = JSON.stringify(formattedLabel);
                    } catch (e) {
                        replacedValue = value;
                    }
                }
                if (value != replacedValue) {
                    labelMessageMapping.add(index.toString(), value);
                }
                return replacedValue;
            });
            if (!labelMessageMapping.isEmpty()) {
                window.setTimeout(
                    () => {
                        const originalResponseCallback = appletInteraction.messageHandler.getResponseCallback(message.MessageID);
                        if (originalResponseCallback) {
                            appletInteraction.messageHandler.registerResponseCallback(message.MessageID, (response: messaging.Responses.GetLabelsResponse) => {
                                $.each(labelMessageMapping.getKeys(), (index, labelIndex) => {
                                    response.LabelResults[labelIndex].ID = labelMessageMapping.get(labelIndex);
                                });
                                originalResponseCallback(response);
                            });
                        }
                    },
                    0);
            }
        } else if (resourceID === appletInteraction.WellKnownResources.Config.ID) {
            window.setTimeout(
                () => {
                    const response = new messaging.Responses.GetConfigResponse();
                    response.Request = message;
                    response.Status = messaging.ResponseStatus.failure;
                    response.FailureReason = "";
                    appletInteraction.messageHandler.listen({ data: response });
                },
                0);
        }
    }

    /** CDS2 properties specified in the app Headers. */
    type Cds2Headers = {
        cds2EnvironmentType: string,
        cds2APIVersion: string
    }

    /** CDS2 authentication properties, a subset of the ServerConfiguration type. */
    type XrmOAuthConfig = {
        authResourceId: string,
        authRealm: string,
        authTenantId: string
    };

    /** Key for the headers property bag to access CDS2 environment type */
    const cds2EnvironmentTypeKey = "cds2EnvironmentType";
    /** Key for the headers property bag to access CDS2 API version */
    const cds2ApiVersionKey = "cds2ApiVersion";

    /**
     * Extracts the CDS2 headers from a property bag.
     * @param headers App headers property bag.
     */
    function getCds2Headers(headers: util.PropertyBag<string>): Cds2Headers {
        if (headers) {
            const env = headers[cds2EnvironmentTypeKey];
            const version = headers[cds2ApiVersionKey];
            if (env && version) {
                return {
                    cds2EnvironmentType: env,
                    cds2APIVersion: version
                };
            }
        }
        return null;
    }

    /**
     * Regex to match CDS 1.0 hostnames, which look like "https://pa-rts-sf-1234.5678.powerapps.com/0123"
     */
    const Cds1HostnameRegex = /^https:\/\/pa-rts-sf.*powerapps\.com(\/.*)?$/;

    /**
     * Regex to match AX one box hostnames, which look like "https://usnconeboxax1aos.cloud.onebox.dynamics.com"
     */
    const AxOneBoxHostnameRegex = /^https:\/\/usnc(onebox)?ax1aos\.cloud\.onebox\.dynamics\.com(\/.*)?$/;

    /**
     * Given a hostname, determine which version of PowerApps to which it refers.
     * @param hostName host to check
     */
    export function determinePowerAppsVersion(hostName: string): PowerAppsVersion {

        // If the hostname looks like a CDS 1.0 hostname, or an AX one box hostname, indicate the version is CDS 1.0
        if (Cds1HostnameRegex.test(hostName) || AxOneBoxHostnameRegex.test(hostName)) {
            return PowerAppsVersion.CDS1;
        } else {
            return PowerAppsVersion.CDS2;
        }
    }

    /**
     * Given a hostname, determine what AppInsights connection string to use
     * @param hostName host to check
     * @returns Application Insights connection string
     */
    export function determineCds2Telemetry(cds2Environment: Environment): string {
        if (cds2Environment === "PROD") {
            return TelemetryConnectionStrings.Prod;
        } else if (cds2Environment === "EU") {
            return TelemetryConnectionStrings.ProdEU;
        } else if (cds2Environment === "TIP") {
            return TelemetryConnectionStrings.PreProd;
        } else if (cds2Environment === "TEST") {
            return TelemetryConnectionStrings.Test
        }

        return TelemetryConnectionStrings.NoTelemetry;
    }

    export function determineCds2AadInstance(cds2Environment: Environment): string {
        if (cds2Environment === "Mooncake") {
            return "https://login.partner.microsoftonline.cn/";
        } else if (cds2Environment === "GCCHigh" || cds2Environment === "USDoD") {
            return "https://login.microsoftonline.us/";
        } else if (cds2Environment === "Eaglex" || cds2Environment === "Raptorx") {
            // Not supported
            return null;
        } else {
            return "https://login.microsoftonline.com/";
        }
    }

    /**
    * Executes the configuration retrieval process. Checks if the server is a valid XRM instance.
    * If this is a CDS2 environment, constructs a server configuration object.
    * If not a CDS2 environment, falls back to the original configuration function which targets /ServerConfiguration.
    * @param originalExecuteConfiguration Function that performs non-PowerApps configuration retrieval
    */
    export function _executeConfiguration(originalExecuteConfiguration: (features?: string[]) => void): void {
        telemetry.operations.retrievingConfiguration.begin(powerAppsResources.dynamicsApp.retrievingConfigurationMessage());
        appState.State.ServerConfig = undefined;
        var errorDetails: any = {};

        const cdsVersion = determinePowerAppsVersion(appState.State.Settings.HostName);
        telemetry.events.powerappsVersionDetermined.log(cdsVersion);

        switch (cdsVersion) {
            case PowerAppsVersion.CDS1:
                originalExecuteConfiguration(["AllowStringBatchResponses"]);
                break;
            case PowerAppsVersion.CDS2:
                // Attempt to get CDS2 configuration from the App Settings
                const cds2Headers = getCds2Headers(appState.State.Settings.Headers);
                const cds2ApiVersion = cds2Headers && cds2Headers.cds2APIVersion || "9.0";
                const dataPath = $format("/api/data/v{0}", cds2ApiVersion);

                const cds2Environment = getEnvironmentFromUrl(appState.State.Settings.HostName);

                // CDS2 config does not use a serverconfig endpoint, but constructs the config offline
                const appInsightsConnectionString = determineCds2Telemetry(cds2Environment);
                const addinId = "2bc50526-cdc3-4e36-a970-c284c34cbd6e";

                const aadRealm = determineCds2AadInstance(cds2Environment);
                if (aadRealm === null) {
                    errorDetails.Environment = cds2Environment;
                    app.serverConfigRetrieved(false, {}, errorDetails, false);
                } else {
                    app.serverConfigRetrieved(true, {
                        Data: <appState.ServerConfiguration>{
                            AppInsightsKey: appInsightsConnectionString || "",                         // varies based on CDS config, default to no telemetry
                            AuthType: appState.AuthType.OAuth,                                         // hardcoded (always OAuth)
                            AuthClientId: addinId,                                                     // hardcoded to the add-in
                            AuthRealm: aadRealm,                                                       // AD login always federated through microsoftonline, tenant unknown so uses multi-tenant login
                            AuthResourceId: appState.State.Settings.HostName,                          // OAuth resource_id is the CDS2 instance
                            // TODO: Find a source for the correct tenant ID, avaliable via WWW-Authenticate but not via JS
                            AuthTenantId: "organizations",                                             // tenant is unknown so use multi-tenant login
                            DataEndpointUrl: dataPath,                                                 // cds2
                            LabelEndpointUrl: "",                                                      // cds2
                            AppletRegistrations: [{                                                    // data connector applet used for cds1/cds2
                                AppletID: "Microsoft.Dynamics.Platform.Integration.Office.DataConnectorApplet",
                                CommonVersion: "1"
                            }] as any,
                            ResourceRegistrations: [{                                                  // cds2 resource definition
                                ResourceID: "Microsoft.Dynamics.Platform.Integration.Office.DataResource",
                                CommonVersion: "CDS2"
                            }] as any,
                            Features: [
                                "EnableAppClientSessionHeader",
                                "OmitEnumMemberQualification",                                         // cds2 does not want enum members qualified in OData
                                "UseStandardODataFilters",                                             // cds2 uses strict OData filtering, no wildcards
                                "FilterEmptyStringAsNull",                                             // cds2 stores "" as null in data
                                "DisableCountRequests"                                                 // platform call for count is limited to 5k records, get around this limitation by not using the getCount call.
                            ]
                        }
                    }, errorDetails, false);
                }

                break;
            default:
                // hitting this default indicates a programming error, a CDS type that was not handled correctly
                telemetry.events.powerappsVersionUnknown.log(cdsVersion);
                app.serverConfigRetrieved(false, { ErrorSource: app._StartAuthenticationCallbackHandler.ServerConfigLocations.Resource, ErrorDetails: powerAppsResources.dynamicsApp.resourceFailedToLoadError() }, errorDetails, false);
                break;
        }
    }
}
